import React, { useEffect, useMemo, useState } from "react";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import Modal from "@/components/Modal/Modal";
import formRenderer from "@/lib/formRenderer";
import {
  getInitialValues,
  requiredMessage,
  onlyNumbersInput,
  pick,
  amountValidation,
  today,
} from "@/lib/utils";
import AuthService from "@/services/api/auth/auth";
import toast from "@/lib/toast";
import styles from "./DeliverOrderModal.module.scss";
import MapView from "@/components/Map/MapView";
import { Divider } from "@mui/material";

interface ApprovedSalesDeliveryModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderData: any;
  onSuccess?: () => void;
  retailerName? : any;
}

interface SalesFormValues {
  orderId: number,
  purchasedAmount: number | string;
  shippingCharges: number | string;
  taxPercent: number | string;
  taxAmount: number | string;
  totalAmount: number | string;
  amountPaid: number | string;
  saleDate: string;
  retailerName: string;
  Address: string;
  latitude: number;
  longitude: number;

  products: {
    warehouseId: number | string;
    productId: number | string;
    quantity: number | string;
    deliverQty: number | string;
    unitPrice: number | string;
    skuCode?: string;
  }[];
}

const ApprovedSalesDeliveryModal: React.FC<ApprovedSalesDeliveryModalProps> = ({
  isOpen,
  onClose,
  orderData,
  onSuccess,
  retailerName
}) => {
  const [mapLocation, setMapLocation] = useState({ lat: 18.96905, lng: 72.82118 });
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [warehouseOptions, setWarehouseOptions] = useState<any[]>([]);
  const [warehouseProductMap, setWarehouseProductMap] = useState<Record<any, any[]>>({});
  const currency = localStorage.getItem("currency");

  const formConfig = useMemo(
    () => ({
      purchasedAmount: {
        defaultValue: "",
        controlType: "input",
        label: `Total Sales (${currency})`,
        isDisabled: true,
        isRequired: true,
      },
      shippingCharges: {
        defaultValue: "",
        controlType: "input",
        label: "Shipping and Handling Charges",
        validations: {
          required: requiredMessage("Shipping Charges"),
          ...amountValidation,
        },
        props: {
          type: "number",
          onKeyDown: onlyNumbersInput,
        },
        isRequired: true,
      },
      taxPercent: {
        defaultValue: "",
        controlType: "input",
        label: "Tax (%)",
        validations: {
          required: requiredMessage("Tax"),
        },
        props: {
          type: "number",
          onKeyDown: onlyNumbersInput,
        },
        isRequired: true,
      },
      taxAmount: {
        defaultValue: "",
        controlType: "input",
        label: "Tax Amount",
        isDisabled: true,
      },
      totalAmount: {
        defaultValue: "",
        controlType: "input",
        label: `Invoice Amount (${currency})`,
        isDisabled: true,
      },
      amountPaid: {
        defaultValue: "",
        controlType: "input",
        label: `Amount Received (${currency})`,
        validations: {
          required: requiredMessage("Amount Paid"),
          ...amountValidation,
          validate: (value: any) => {
            const invoiceAmount = Number(getValues("totalAmount")) || 0;

            if (Number(value) > invoiceAmount) {
              return "Amount received cannot be greater than invoice amount";
            }
            return true;
          },
        },  
        props: {
          type: "number",
          onKeyDown: onlyNumbersInput
        },
        isRequired: true,
      },
      saleDate: {
        controlType: "date",
        label: "Sales Date",
        placeholder: "Enter Date",
        isRequired: true,
        props: {
          min: today,
        }
      },
      retailerName: {
        defaultValue: "",
        controlType: "input",
        label: "Retailer Name",
        isRequired: true,
        isDisabled: true,
      },
      Address: {
        defaultValue: "",
        validations: { required: requiredMessage("Address") },
        controlType: "input",
        label: "Address",
        isRequired: true,
        onFieldClick: () => setMapModalOpen(true),
        className: styles.fullRow,
      },
      Latitude: {
        defaultValue: "",
        controlType: "hidden",
        label: "Latitude",
      },
      Longitude: {
        defaultValue: "",
        controlType: "hidden",
        label: "Longitude",
      },
    }),
    []
  );

  const getFilteredProducts = (warehouseId: any, rowIndex: number) => {
    const allProducts = warehouseProductMap[warehouseId] || [];
    const selectedProductIds = watchedProducts
      .filter((_: any, i: number) => i !== rowIndex)
      .filter((p: any) => Number(p.warehouseId) === Number(warehouseId))
      .map((p: any) => Number(p.productId));

    return allProducts.filter(
      (p: any) => !selectedProductIds.includes(Number(p.value))
    );
  };

  const { register, handleSubmit, control, watch, setValue, getValues, formState, reset } =
    useForm<SalesFormValues>({
      defaultValues: {
        ...getInitialValues(formConfig),
        products: [
          {
            warehouseId: "",
            productId: "",
            quantity: "",
            deliverQty: "",
            unitPrice: "",
          },
        ],
      },
      mode: "onTouched",
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "products",
  });

  const watchedProducts = useWatch({ control, name: "products" });
  const watchedShippingCharges = useWatch({ control, name: "shippingCharges" });
  const watchedTaxPercent = useWatch({ control, name: "taxPercent" });

  const fetchWarehouseByOrder = async (orderId: number) => {
    try {
      const res = await AuthService.getspecificorderDetails(orderId)
      const warehouses = res?.data || [];

      const warehouseOpts = warehouses.map((w: any) => ({
        label: w.warehouseCode, 
        value: w.warehouseId,
      }));

      const map: Record<number, any[]> = {};
      warehouses.forEach((w: any) => {
        map[w.warehouseId] = w.products.map((p: any) => ({
          label: p.productName,
          value: p.productId,
          skuCode: p.skuCode,
          quantity: p.quantity,
        }));
      });

      setWarehouseOptions(warehouseOpts);
      setWarehouseProductMap(map);
    } catch (err) {
      toast.error("Failed to load warehouse details");
    }
  };

  useEffect(() => {
    if (isOpen && orderData?.id) {
      fetchWarehouseByOrder(orderData.id);
    }
  }, [isOpen, orderData]);

  useEffect(() => {
    watchedProducts?.forEach((row, index) => {
      if (!row.warehouseId || !row.productId) return;

      const selectedProduct =
        warehouseProductMap[row.warehouseId]?.find(
          (p: any) => p.value === row.productId
        );

      if (!selectedProduct) return;

      setValue(`products.${index}.quantity`, selectedProduct.quantity);
      setValue(`products.${index}.skuCode`, selectedProduct.skuCode);
    });
  }, [JSON.stringify(watchedProducts), warehouseProductMap]);

  useEffect(() => {
    if (isOpen && orderData) {
      const initialProducts =
        orderData.orderDetails && Array.isArray(orderData.orderDetails)
          ? orderData.orderDetails.map((detail: any) => ({
            productId: detail.productId || detail.distributorProductId,
            quantity: detail.quantity || 0,
            unitPrice: detail.price || 0,
            skuCode: detail.skuCode || "",
          }))
          : [{ warehouseId: "", productId: "", quantity: "", unitPrice: "", deliverQty: "" }];
      reset({
        ...getInitialValues(formConfig),
        orderId: orderData.id,
        retailerName: retailerName || "",
        products: initialProducts,
      });
      // setValue("retailerName", orderData.retailerName || "");
    }
  }, [isOpen, orderData, reset, formConfig]);

  useEffect(() => {
    const purchasedAmount = watchedProducts.reduce((sum, row) => {
      const deliveredQty = Number(row?.deliverQty) || 0;
      const unitPrice = Number(row?.unitPrice) || 0;
      return sum + deliveredQty * unitPrice;
    }, 0);

    setValue("purchasedAmount", purchasedAmount, { shouldValidate: false });

    const shippingCharges = Number(watchedShippingCharges) || 0;
    const taxPercent = Number(watchedTaxPercent) || 0;
    const taxAmount = purchasedAmount * taxPercent / 100;

    setValue("taxAmount", taxAmount, { shouldValidate: false });
    const totalAmount = purchasedAmount + shippingCharges + taxAmount;
    setValue("totalAmount", totalAmount, { shouldValidate: false });
  }, [JSON.stringify(watchedProducts), watchedShippingCharges, watchedTaxPercent, setValue]);

  const getProductRowConfig = (index: number) => ({
    [`products.${index}.warehouseId`]: {
      controlType: "select",
      label: "Warehouse Code",
      placeholder: "Select Warehouse",
      options: warehouseOptions,
      isControlled: true,
      isRequired: true,
      rules: { required: requiredMessage("Warehouse Code") },
      onChange: () => {
        setValue(`products.${index}.productId`, "");
        setValue(`products.${index}.quantity`, "");
        setValue(`products.${index}.deliverQty`, "");
      },
    },
    [`products.${index}.productId`]: {
      controlType: "select",
      label: "Product Code",
      placeholder: "Select Product",
      isControlled: true,
      isRequired: true,
      options: getFilteredProducts(
        watch(`products.${index}.warehouseId`),
        index
      ),
      rules: { required: requiredMessage("Product") },

      onChange: (productId: any) => {
        const warehouseId = watch(`products.${index}.warehouseId`);
        const selectedProduct =
          warehouseProductMap[warehouseId]?.find(
            (p: any) => Number(p.value) === Number(productId)
          );

        if (selectedProduct) {
          setValue(`products.${index}.quantity`, selectedProduct.quantity);
          setValue(`products.${index}.deliverQty`, selectedProduct.quantity);
          setValue(`products.${index}.skuCode`, selectedProduct.skuCode);
        }
      },
    },

    [`products.${index}.quantity`]: {
      defaultValue: "0",
      controlType: "input",
      label: "Ordered Quantity",
      isControlled: true,
      placeholder: "",
      isRequired: true,
      isDisabled: true,
      props: {
        type: "number",
        onKeyDown: onlyNumbersInput,
      },
      rules: { required: requiredMessage("Quantity") },
    },
    [`products.${index}.deliverQty`]: {
      controlType: "input",
      label: "Delivered Quantity",
      isRequired: true,
      isControlled: true,
      props: {
        type: "number",
        onKeyDown: onlyNumbersInput,
      },
      rules: { required: requiredMessage("Quantity"),
        validate: (value: any) => {
          const orderedQty = Number(
            watch(`products.${index}.quantity`)
          ) || 0;

          if (Number(value) > orderedQty) {
            return "Delivered quantity cannot be greater than ordered quantity";
          }
          return true;
        },
       }
    },
    [`products.${index}.unitPrice`]: {
      defaultValue: "",
      controlType: "input",
      label: `Selling Unit Price (${currency})`,
      placeholder: "Enter Unit Price",
      isRequired: true,
      isControlled: true,
      props: {
        type: "number",
        onKeyDown: onlyNumbersInput,
      },
      rules: { required: requiredMessage("selling price") }
    },

  });

  const onSubmit = async (values: any) => {
    try {
      const invoicePayload = {
        invoiceNumber: "",
        orderId: Number(orderData.id),
        salesDate: values.saleDate,
        shippingAddress: values.Address,
        shippingHandlingFees: Number(values.shippingCharges || 0),
        Latitude: values.latitude,
        Longitude: values.longitude,
        taxPercentage: Number(values.taxPercent || 0),
        taxAmount: Number(values.taxAmount || 0),
        totalSales: Number(values.purchasedAmount || 0),
        invoiceAmount: Number(values.totalAmount || 0),
        amountReceived: Number(values.amountPaid || 0),
        retailerName:orderData.retailerName,
        retailerEmailId: values.retailerEmailId,
        salesItems: values.products.map((product: any) => {
          const warehouseProducts = warehouseProductMap[product.warehouseId] || [];
          const selectedProduct = warehouseProducts.find((p: any) => Number(p.value) === Number(product.productId));

          return {
            warehouseId: Number(product.warehouseId || 0),
            productId: Number(product.productId),
            productName: selectedProduct?.label || "",
            skuCode: selectedProduct?.skuCode || "",
            deliveredQty: Number(product.deliverQty),
            sellingPrice: Number(product.unitPrice),
          };
        }),
      };

      const response = await AuthService.AddDeliveredOrder(invoicePayload);
      if (response?.status === 200) {
        toast.success("Order delivered successfully!");
        handleClose();
        onSuccess?.();
      } else {
        toast.error(response?.error || "Failed to deliver order");
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error ||
        error?.message ||
        "Failed to deliver order"
      );
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSelectLocation = (lat: number, lng: number, addr: string) => {
    setMapLocation({ lat, lng });
    setValue("Address", addr, { shouldValidate: true, shouldDirty: true });
    setValue("latitude", lat);
    setValue("longitude", lng);
  };

  return (
    <Modal
      modalOpen={isOpen}
      closeModal={handleClose}
      heading="Add Sales"
      width="1200px"
      saveButtonText="Add"
      onSave={handleSubmit(onSubmit)}
      isSubmitting={formState.isSubmitting}
    >
      <form onSubmit={handleSubmit(onSubmit)} className={styles.formContainer}>
        <div className={styles.summaryGrid}>
          {formRenderer(
            pick(formConfig, [
              "saleDate",
              "retailerName",
              "Address",
            ]),
            formState,
            register,
            control
          )}
        </div>
        <Divider sx={{ marginTop: 3, marginBottom: 3 }}></Divider>
        {/* Products */}
        <h4>Add Products</h4>
        {fields.map((field, index) => {
          const row = watchedProducts[index];
          const isRowFilled = row?.productId && row?.quantity;
          const isLastRow = index === fields.length - 1;
          const canDelete = fields.length > 1;
          const showAddButton = isLastRow && isRowFilled;

          return (
            <div key={field.id}>
              <div className={styles.fourCol}>
                {formRenderer(
                  getProductRowConfig(index),
                  formState,
                  register,
                  control
                )}
              </div>

              <div className={styles.rowActions}>
                {showAddButton && (
                  <span
                    className={styles.addMore}
                    onClick={() =>
                      append({
                        warehouseId: "",
                        productId: "",
                        quantity: "",
                        unitPrice: "",
                        deliverQty: "",
                      })
                    }
                  >
                    Add more
                  </span>
                )}

                {canDelete && (
                  <span
                    className={styles.deleteRow}
                    onClick={() => remove(index)}
                  >
                    Delete
                  </span>
                )}
              </div>
            </div>
          );
        })}


        {/* Summary */}
        {/* <h4>Order Details</h4> */}
        <Divider sx={{ marginTop: 3, marginBottom: 3 }}></Divider>
        <div className={styles.twoCol}>
          {formRenderer(
            pick(formConfig, [
              "purchasedAmount",
              "shippingCharges",
              "taxPercent",
              "taxAmount",
              "totalAmount",
              "amountPaid",
            ]),
            formState,
            register,
            control
          )}
        </div>
      </form>
      <Modal
        modalOpen={mapModalOpen}
        closeModal={() => setMapModalOpen(false)}
        heading="Select Address on Map"
        width="800px"
        onSave={() => setMapModalOpen(false)}
      >
        <MapView
          defaultLat={mapLocation.lat}
          defaultLng={mapLocation.lng}
          onSelectLocation={handleSelectLocation}
          showSearchBar={true}
        />
      </Modal>
    </Modal>
  );
};

export default ApprovedSalesDeliveryModal;
